// src/CompositorCanvas.tsx
import { useMergeRefs } from "@chakra-ui/react-use-merge-refs";
import { forwardRef } from "react";

// src/useCompositorControls.ts
import { createContext, useContext } from "react";
var Context = createContext(
  void 0
);
Context.displayName = "CompositorContext";
var { Provider } = Context;
var useCompositorContext = () => {
  const context = useContext(Context);
  if (!context) {
    const error = new Error(
      "useCompositorContext: `context` is undefined, did you forget to wrap this component in a `CompositorProvider`"
    );
    error.name = "CompositorContextError";
    Error.captureStackTrace?.(error, useContext);
    throw error;
  }
  return context;
};
var useCompositorControls = () => {
  const {
    aspectRatio,
    deselectAllRegions,
    download,
    hoveredRegion,
    isLoading,
    markedRegions,
    resetAllTextures,
    scale,
    selectRegion,
    selectedRegion,
    setMarkedRegions,
    setRegionTexture,
    setScale
  } = useCompositorContext();
  return {
    aspectRatio,
    deselectAllRegions,
    download,
    hoveredRegion,
    isLoading,
    markedRegions,
    resetAllTextures,
    scale,
    selectRegion,
    selectedRegion,
    setMarkedRegions,
    setRegionTexture,
    setScale
  };
};

// src/CompositorCanvas.tsx
import { jsx } from "react/jsx-runtime";
var CompositorCanvas = forwardRef(
  ({ style, height = "100%", width = "100%", ...props }, forwardedRef) => {
    const {
      containerRef,
      canvasProps: { style: canvasStyle, ref: canvasRef }
    } = useCompositorContext();
    const ref = useMergeRefs(canvasRef, forwardedRef);
    return /* @__PURE__ */ jsx(
      "div",
      {
        ref: containerRef,
        style: {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width,
          height,
          ...style
        },
        children: /* @__PURE__ */ jsx("canvas", { style: canvasStyle, ...props, ref })
      }
    );
  }
);

// src/useCompositor.ts
import { useSizes } from "@chakra-ui/react-use-size";
import { assertUnreachable, ensureError } from "@hover/utilities";
import {
  Compositor,
  DEFAULT_ASPECT_RATIO,
  DEFAULT_SCALE
} from "@hoverinc/rendering-compositor";
import {
  useCallback as useCallback2,
  useEffect as useEffect2,
  useMemo,
  useRef as useRef2,
  useState
} from "react";

// src/useLatest.ts
import { useCallback, useEffect, useRef } from "react";
var useLatest = (callback, dependencies = []) => {
  const ref = useRef(callback);
  useEffect(() => {
    ref.current = callback;
  }, [callback, ...dependencies]);
  return useCallback((...args) => ref.current(...args), []);
};

// src/useCompositor.ts
var forwardedMethodNames = ["resetAllTextures", "download"];
var forwardCompositorMethod = (compositor, method) => (...args) => compositor ? compositor[method](...args) : void 0;
var emptyScene = {
  beauty: "",
  lighting: "",
  matte: "",
  uv: ""
};
var useCompositor = ({
  fadeSpeed,
  flashColor,
  highlightColor,
  initialScale,
  isAnimated = true,
  maxScale = 1,
  onClick,
  onLoadError,
  onMouseDown,
  onMouseMove,
  onMouseUp,
  scene,
  selectionColor,
  sizeMode = "width",
  transitionSpeed
}) => {
  const hasScene = !!scene;
  const {
    allowedRegions,
    aspectRatio: sceneAspectRatio = DEFAULT_ASPECT_RATIO,
    beauty,
    scale: sceneScale = initialScale ?? DEFAULT_SCALE,
    lighting,
    matte,
    rotatedRegions = [],
    uv
  } = hasScene ? scene : emptyScene;
  const [resolution, setResolution] = useState({
    width: 0,
    height: 0
  });
  const [actualResolution, setActualResolution] = useState({
    width: 0,
    height: 0
  });
  const [compositor, setCompositor] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [scale, setScale] = useState(sceneScale);
  const [aspectRatio, setAspectRatio] = useState(sceneAspectRatio);
  const [markedRegions, setMarkedRegions] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [hoveredRegion, setHoveredRegion] = useState(null);
  const onMouseLeave = useCallback2(() => {
    setHoveredRegion(null);
  }, []);
  const containerRef = useRef2(null);
  const internalRef = useRef2(null);
  const canvasRef = useCallback2(
    (element) => {
      internalRef.current?.removeEventListener("mouseleave", onMouseLeave);
      element?.addEventListener("mouseleave", onMouseLeave);
      setCompositor(
        element ? new Compositor(element, {
          animate: isAnimated,
          flashColor,
          highlightColor,
          selectionColor,
          fadeSpeed,
          transitionSpeed,
          updateStyles: false
        }) : null
      );
      internalRef.current = element;
    },
    [
      fadeSpeed,
      flashColor,
      highlightColor,
      isAnimated,
      onMouseLeave,
      selectionColor,
      transitionSpeed
    ]
  );
  const [containerSize, canvasSize] = useSizes({
    getNodes: () => [containerRef.current, internalRef.current]
  });
  const getAllowedRegion = useCallback2(
    (id) => {
      if (!allowedRegions)
        return id;
      return allowedRegions.includes(id) ? id : null;
    },
    [allowedRegions]
  );
  const setRegionTexture = useCallback2(
    async (texture, { rotate, ...options }) => {
      if (selectedRegion && compositor) {
        await compositor.setRegionTexture(selectedRegion, texture, {
          ...options,
          animate: isAnimated,
          rotate: rotate ?? rotatedRegions.includes(selectedRegion)
        });
      }
    },
    [compositor, isAnimated, rotatedRegions, selectedRegion]
  );
  const selectRegion = useCallback2(
    (id) => setSelectedRegion(getAllowedRegion(id)),
    [setSelectedRegion, getAllowedRegion]
  );
  const deselectAllRegions = useCallback2(
    () => setSelectedRegion(null),
    [setSelectedRegion]
  );
  const onCanvasClick = useCallback2(
    (id, coordinates) => {
      setSelectedRegion(getAllowedRegion(id));
      onClick?.(id, coordinates);
    },
    [getAllowedRegion, onClick]
  );
  const onCanvasMouseMove = useCallback2(
    (id, coordinates) => {
      const region = getAllowedRegion(id);
      if (region !== hoveredRegion) {
        setHoveredRegion(region);
      }
      onMouseMove?.(id, coordinates);
    },
    [getAllowedRegion, hoveredRegion, onMouseMove]
  );
  useEffect2(
    () => selectedRegion ? compositor?.selectRegion(selectedRegion) : compositor?.deselectAllRegions(),
    [compositor, selectedRegion]
  );
  useEffect2(
    () => hoveredRegion ? compositor?.highlightRegion(hoveredRegion) : compositor?.clearRegionHighlight(),
    [compositor, hoveredRegion, selectedRegion]
  );
  useEffect2(
    () => compositor?.markRegions(markedRegions),
    [compositor, markedRegions]
  );
  useEffect2(() => compositor?.scale(scale), [compositor, scale]);
  const forwardedMethods = useMemo(
    () => forwardedMethodNames.reduce(
      (acc, methodName) => ({
        ...acc,
        [methodName]: forwardCompositorMethod(compositor, methodName)
      }),
      {}
    ),
    [compositor]
  );
  useEffect2(() => {
    compositor?.setClickHandler(onCanvasClick);
  }, [compositor, onCanvasClick]);
  useEffect2(() => {
    compositor?.setMouseMoveHandler(onCanvasMouseMove);
  }, [compositor, onCanvasMouseMove]);
  useEffect2(() => {
    if (onMouseDown)
      compositor?.setMouseDownHandler(onMouseDown);
  }, [compositor, onMouseDown]);
  useEffect2(() => {
    if (onMouseUp)
      compositor?.setMouseUpHandler(onMouseUp);
  }, [compositor, onMouseUp]);
  const onLoadErrorLatest = useLatest(onLoadError ?? (() => {
  }));
  useEffect2(() => {
    const load = async () => {
      if (!compositor || !hasScene)
        return;
      setIsLoading(true);
      setMarkedRegions([]);
      setSelectedRegion(null);
      setScale(sceneScale);
      setAspectRatio(sceneAspectRatio);
      try {
        await compositor.load(beauty, matte, lighting, uv, sceneScale);
      } catch (e) {
        onLoadErrorLatest(ensureError(e));
      } finally {
        setIsLoading(false);
        if (compositor.aspectRatio)
          setAspectRatio(compositor.aspectRatio);
        setActualResolution({
          width: compositor.actualWidth,
          height: compositor.actualHeight
        });
      }
    };
    void load();
  }, [
    beauty,
    compositor,
    hasScene,
    lighting,
    matte,
    onLoadErrorLatest,
    sceneAspectRatio,
    sceneScale,
    uv
  ]);
  const observedWidth = containerSize?.width ?? canvasSize?.width;
  const observedHeight = containerSize?.height ?? canvasSize?.height;
  useEffect2(() => {
    switch (sizeMode) {
      case "width": {
        if (observedWidth) {
          const height2 = observedWidth / aspectRatio;
          compositor?.resize(observedWidth, height2);
          setResolution({ width: observedWidth, height: height2 });
        }
        break;
      }
      case "height": {
        if (observedHeight && observedWidth) {
          const width2 = observedHeight * aspectRatio;
          compositor?.resize(width2, observedHeight);
          setResolution({ width: width2, height: observedHeight });
        }
        break;
      }
      case "contain": {
        if (observedHeight && observedWidth) {
          if (!containerRef.current) {
            console.warn(
              "useCompositor: `contain` size mode is only supported with `CompositorCanvas` or when a `containerRef` to the canvas parent element is provided"
            );
          }
          const { width: actualWidth, height: actualHeight } = actualResolution;
          const constrainedWidth = Math.min(
            actualWidth * maxScale,
            observedWidth
          );
          const constrainedHeight = Math.min(
            actualHeight * maxScale,
            observedHeight
          );
          const observedAspectRatio = observedWidth / observedHeight;
          const letterbox = observedAspectRatio < aspectRatio;
          const aspectWidth = constrainedHeight * aspectRatio;
          const aspectHeight = constrainedWidth / aspectRatio;
          const width2 = letterbox ? constrainedWidth : aspectWidth;
          const height2 = letterbox ? aspectHeight : constrainedHeight;
          compositor?.resize(width2, height2);
          setResolution({ width: width2, height: height2 });
        }
        break;
      }
      default:
        assertUnreachable(sizeMode);
        break;
    }
  }, [
    actualResolution,
    aspectRatio,
    compositor,
    maxScale,
    observedHeight,
    observedWidth,
    sizeMode
  ]);
  const { width, height } = resolution;
  const dimensions = containerRef.current ? { width, height } : { width: sizeMode === "width" ? "100%" : void 0 };
  const canvasProps = {
    ref: canvasRef,
    style: {
      cursor: hoveredRegion ? "pointer" : "default",
      display: "block",
      ...dimensions
    }
  };
  return {
    aspectRatio,
    canvasProps,
    containerRef,
    deselectAllRegions,
    hoveredRegion,
    isLoading,
    markedRegions,
    scale,
    selectRegion,
    selectedRegion,
    setMarkedRegions,
    setRegionTexture,
    setScale,
    ...forwardedMethods
  };
};

// src/CompositorProvider.tsx
import { jsx as jsx2 } from "react/jsx-runtime";
var CompositorProvider = ({
  children,
  ...props
}) => {
  const compositor = useCompositor(props);
  return /* @__PURE__ */ jsx2(Provider, { value: compositor, children });
};

// src/index.ts
import { DEFAULT_COLOR, DEFAULT_SCALE as DEFAULT_SCALE2 } from "@hoverinc/rendering-compositor";
export {
  CompositorCanvas,
  CompositorProvider,
  DEFAULT_COLOR,
  DEFAULT_SCALE2 as DEFAULT_SCALE,
  useCompositor,
  useCompositorContext,
  useCompositorControls
};
//# sourceMappingURL=index.mjs.map